import { BREAKPOINTS, Button, Dropdown, H2, MessageCard, Modal, TextInput, useMedia } from '@manulife/mux';
import { ButtonRightIcon } from '@manulife/mux-cds-icons';
import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import useAEMContent, { FRAGMENT_MP } from '../../CustomHooks/useAEMContent';
import useShallowEqualSelector from '../../CustomHooks/useShallowEqualSelector';
import DropDownListLoadingIndicator from '../LoadingIndicator/DropDownListLoadingIndicator';
import { ButtonWrapper, ErrorMsg, InfoMessageWrapper, TextInputWrapper } from '../Shared/SharedStyles';
import { GetCanadaPostAddressDetails } from './ReducerActions/GetCanadaPostAddressDetails';
import { GetCanadaPostAddressSuggestion } from './ReducerActions/GetCanadaPostAddressSuggestion';
import { ResetCanadaPostAddressDetail } from './ReducerActions/ResetCanadaPostAddressDetail';
import { ResetCanadaPostAddressSuggestion } from './ReducerActions/ResetCanadaPostAddressSuggestion';
import { UpdateMemberPhoneNumberAndPostalAddress } from './ReducerActions/UpdateMemberPhoneNumberAndPostalAddress';
import { ADOBE } from '../../utils/AppConstants';

const KEYS = {
    ENTER: 13,
    UP: 38,
    DOWN: 40,
};

const AutoSuggestionWrapper = styled.div`
    background-color: white;
    margin-top: 81px;
    max-height: 270px;
    width: 43%;
    height: auto;
    z-index: 9;
    border: 1px solid #000;
    position: absolute;
    overflow: auto;
`;

const AddressTextWithoutIconWrapper = styled.div`
    width: 100%;
`;

const AddressTextWrapper = styled.div`
    width: 80%;
`;

const AddressIconWrapper = styled.div`
    width: 15%;
    margin: auto;
`;

const AddressText = styled.span`
    font-size: 14px;
    font-weight: 600;
`;

const AddressDescription = styled.span`
    font-size: 12px;
    font-style: italic;
`;

const AddressNotFound = styled.span`
    font-size: 12px;
    text-decoration: underline;
    color: black;
`;

const AutoSuggestionList = styled.div`
    display: flex;
    cursor: pointer;
    padding: 1px 0px 1px 8px;
    &:hover {
        background-color: #1d90ff;
        color: black;
    }
`;

const AutoSuggestionListActive = styled.div`
    display: flex;
    cursor: pointer;
    padding: 1px 0px 1px 8px;
    background-color: #1d90ff;
    color: black;
`;

const ContactInfoModal = () => {
    const history = useHistory();
    const {
        features,
        postalAddress,
        isSavingPostalAddressInformation,
        error,
        isCsr,
        suggestionDropDownAddressList,
        dropDownSelectedAddressDetail,
        isLoadingSuggestionDropDownAddressList,
        isLoadingdropDownSelectedAddressDetail,
        isCiamSession,
        isSteppedUp,
    } = useShallowEqualSelector((state) => ({
        features: state.Features.data,
        postalAddress: state.MemberData.member.postalAddress,
        isSavingPostalAddressInformation: state.MemberData.isSavingPostalAddressInformation,
        error: state.Errors.postalAddressUpdate,
        isCsr: state.Authentication.isCsr,
        suggestionDropDownAddressList: state.CanadaPostAddress.suggestionDropDownAddressList,
        dropDownSelectedAddressDetail: state.CanadaPostAddress.dropDownSelectedAddressDetail,
        isLoadingSuggestionDropDownAddressList: state.CanadaPostAddress.isLoadingSuggestionDropDownAddressList,
        isLoadingdropDownSelectedAddressDetail: state.CanadaPostAddress.isLoadingdropDownSelectedAddressDetail,
        isCiamSession: state.Authentication.isCiamSession,
        isSteppedUp: state.Authentication.isSteppedUp,
    }));

    const dropDownRef = useRef(null);
    const scrollToDropDownActiveElement = useRef([]);
    const [canadaPostAddressDropdownActiveIndex, setCanadaPostAddressDropdownActiveIndex] = useState(0);
    const [showCanadaPostAddressDropDown, setShowCanadaPostAddressDropDown] = useState(true);

    const dispatch = useDispatch();

    const mobileView = useMedia([`(max-width: ${BREAKPOINTS.TABLET_PORTRAIT}px)`], [true], false);
    const customFieldStyle = mobileView ? { width: '100%' } : {};

    const selectLabel = useAEMContent(FRAGMENT_MP.common, 'select');
    const saveButton = useAEMContent(FRAGMENT_MP.common, 'saveButton');
    const cancelButton = useAEMContent(FRAGMENT_MP.common, 'cancelButton');

    const countryLabel = useAEMContent(FRAGMENT_MP.contactInfo, 'countryLabel');
    const cityLabel = useAEMContent(FRAGMENT_MP.contactInfo, 'cityLabel');
    const header = useAEMContent(FRAGMENT_MP.contactInfo, 'header');
    const address1Label = useAEMContent(FRAGMENT_MP.contactInfo, 'address1Label');
    const addressNotFoundLabel = useAEMContent(FRAGMENT_MP.contactInfo, 'addressNotFound');
    const streetAddressLabel = useAEMContent(FRAGMENT_MP.contactInfo, 'streetAddressPlaceholderLabel');
    const address2Label = useAEMContent(FRAGMENT_MP.contactInfo, 'address2Label');
    const apartmentOrUnitLabel = useAEMContent(FRAGMENT_MP.contactInfo, 'apartmentUnitNumberLabel');
    const provinceLabel = useAEMContent(FRAGMENT_MP.contactInfo, 'provinceLabel');
    const postalCodeLabel = useAEMContent(FRAGMENT_MP.contactInfo, 'postalCodeLabel');
    const postalCodePlaceholder = useAEMContent(FRAGMENT_MP.contactInfo, 'postalCodePlaceholder');
    const phoneNumberLabel = useAEMContent(FRAGMENT_MP.contactInfo, 'phoneNumberLabel');
    const phoneNumberPlaceholder = useAEMContent(FRAGMENT_MP.contactInfo, 'phoneNumberPlaceholder');
    const InformationMessage = useAEMContent(FRAGMENT_MP.contactInfo, 'InformationMessage', { parseHTML: true });

    const canadaCountryLabel = useAEMContent(FRAGMENT_MP.countries, 'canada');
    const usaCountryLabel = useAEMContent(FRAGMENT_MP.countries, 'usa');

    const albertProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'ab');
    const britishColumbiaProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'bc');
    const manitobaProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'mb');
    const newBrunswickProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'nb');
    const newfoundlandProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'nl');
    const novaScotiaProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'ns');
    const northwestTerritoriesProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'nt');
    const nunavutTerritoriesProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'nu');
    const ontarioProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'on');
    const princeEdwardIslandProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'pe');
    const quebecProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'qc');
    const saskatchewanProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'sk');
    const yukonTerritoriesProvinceLabel = useAEMContent(FRAGMENT_MP.provinces, 'yt');

    const incompleteErrorMessage = useAEMContent(FRAGMENT_MP.validationMessages, 'missingInfo');
    const incorrectPhoneNumberErrorMessage = useAEMContent(FRAGMENT_MP.validationMessages, 'minDigits');
    const incorrectPostalCodeErrorMessage = useAEMContent(FRAGMENT_MP.validationMessages, 'invalidPostalCode');

    const validationSchema = Yup.object().shape({
        country: Yup.string().when([], {
            is: () => features['postalAddress'],
            then: Yup.string().required(incompleteErrorMessage),
        }),
        streetAddress: Yup.string().when([], {
            is: () => features['postalAddress'],
            then: Yup.string().required(incompleteErrorMessage),
        }),
        city: Yup.string().when([], {
            is: () => features['postalAddress'],
            then: Yup.string().required(incompleteErrorMessage),
        }),
        province: Yup.string().when([], {
            is: () => features['postalAddress'],
            then: Yup.string().required(incompleteErrorMessage),
        }),
        postalCode: Yup.string().when([], {
            is: () => features['postalAddress'],
            then: Yup.string()
                .matches(/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/, incorrectPostalCodeErrorMessage)
                .required(incompleteErrorMessage),
        }),
        phoneNumber: Yup.string().when([], {
            is: () => features['phoneNumber'],
            then: Yup.string()
                .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, incorrectPhoneNumberErrorMessage)
                .required(incompleteErrorMessage),
        }),
    });
    const sendAdobeAnalyticsTransaction = (transactionName) => {
                    window.adobeDataLayer?.push({
                        event: ADOBE.TRANSACTION_COMPLETE,
                        transaction: {
                            transactionName,
                            transactionCategory: 'Member Profile',
                        }
                    });
                };

    const { errors, handleSubmit, setFieldTouched, setFieldValue, touched, values } = useFormik({
        initialValues: {
            streetAddress: postalAddress?.streetAddress ? postalAddress.streetAddress : '',
            suite: postalAddress?.suite ? postalAddress.suite : '',
            city: postalAddress?.city ? postalAddress.city : '',
            province: postalAddress?.stateOrProvince ? postalAddress.stateOrProvince : '',
            country: postalAddress?.country ? postalAddress.country : '',
            postalCode: postalAddress?.postalOrZipCode ? postalAddress.postalOrZipCode.replace(/\s\s+/g, ' ') : '',
            // Strip the non-digit characters then format to telephone number.
            phoneNumber: postalAddress?.phoneNumber
                ? postalAddress.phoneNumber.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
                : '',
        },
        onSubmit: async () => {
            const response = await dispatch(
                UpdateMemberPhoneNumberAndPostalAddress(
                    values.streetAddress,
                    values.suite,
                    values.city,
                    values.province,
                    values.country,
                    values.postalCode,
                    // Strip the non-digit characters before submitting.
                    values.phoneNumber.replace(/\D/g, '')
                )
            );
            // Only close the modal after the member's postal address or phone number has been successfully updated
            if (response.success) {
                history.push('/gb/member-profile-2/personal');
            }

            dispatch(ResetCanadaPostAddressDetail());
            dispatch(ResetCanadaPostAddressSuggestion());
        },
        validationSchema,
    });

    useEffect(() => {
        if (isCiamSession && !isSteppedUp) {
            history.push('/gb/member-profile-2/personal');
        }
    }, [history, isCiamSession, isSteppedUp]);

    /**
     * Every time address is selected from canada post auto suggestion drop down it will call canada post api with id
     * of the address to retrieve city, province and postal code of that address and store in
     * dropDownSelectedAddressDetail and here it is getting set in form.
     */
    useEffect(() => {
        if (Object.keys(dropDownSelectedAddressDetail).length > 0) {
            setFieldValue('city', dropDownSelectedAddressDetail.City);
            setFieldValue('province', dropDownSelectedAddressDetail.ProvinceCode);
            setFieldValue('postalCode', dropDownSelectedAddressDetail.PostalCode);
        }
    }, [dropDownSelectedAddressDetail, setFieldValue]);

    /**
     * Scroll the Canada Post Address suggestion dropdown to active element cause whatever element of the dropdown user
     * navigate through keyboard should be visible in dropdown. Content of the address dropdown is bigger than its
     * height so it has scroll now all addresses in dropdown can not be visible at once so this useEffect keep track of
     * the active element and scroll the dropdown to it.
     */
    useEffect(() => {
        if (
            (touched.streetAddress || (suggestionDropDownAddressList && suggestionDropDownAddressList.length > 0)) &&
            scrollToDropDownActiveElement.current[canadaPostAddressDropdownActiveIndex]
        ) {
            scrollToDropDownActiveElement.current[canadaPostAddressDropdownActiveIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }
    }, [canadaPostAddressDropdownActiveIndex, suggestionDropDownAddressList, touched.streetAddress]);

    /**
     * Scroll the Canada Post Address suggestion dropdown to active element cause whatever element of the dropdown user
     * navigate through keyboard should be visible in dropdown. Content of the address dropdown is bigger than its
     * height so it has scroll now all addresses in dropdown can not be visible at once so this useEffect keep track of
     * the active element and scroll the dropdown to it.
     */
    useEffect(() => {
        if (touched.streetAddress && scrollToDropDownActiveElement.current[canadaPostAddressDropdownActiveIndex]) {
            scrollToDropDownActiveElement.current[canadaPostAddressDropdownActiveIndex].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }
    }, [canadaPostAddressDropdownActiveIndex, touched.streetAddress]);

    /**
     * It will be called when clicked outside of dropdown to close it.
     */
    const handleClickOutsideDropdown = (event) => {
        if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
            setShowCanadaPostAddressDropDown(false);
            dispatch(ResetCanadaPostAddressSuggestion());
        }
    };

    /**
     * This useEffect keep track of click event in modal to detect dropdown outside click.
     */
    useEffect(() => {
        document.addEventListener('click', handleClickOutsideDropdown, true);
        // cleanup this component
        return () => {
            document.removeEventListener('click', handleClickOutsideDropdown, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Modal
            ariaLabelledBy="heading"
            id="editContactModal"
            isOpen={true}
            onClose={() => {
                history.push('/gb/member-profile-2/personal');
                dispatch(ResetCanadaPostAddressDetail());
                dispatch(ResetCanadaPostAddressSuggestion());
            }}
        >
            <H2 id="heading" customStyle={{ elementStyle: { marginTop: '0', marginBottom: '12px' } }}>
                {header}
            </H2>
            <InfoMessageWrapper>
                <MessageCard variant={MessageCard.VARIANT.SECONDARY_INFORMATION}>{InformationMessage}</MessageCard>
            </InfoMessageWrapper>
            {error?.hasError && (
                <ErrorMsg>
                    {error.message} Code: {error.code}
                </ErrorMsg>
            )}

            <form onSubmit={handleSubmit}>
                <Dropdown
                    dropdownItems={[
                        {
                            label: canadaCountryLabel,
                            value: 'CA',
                        },
                        {
                            label: usaCountryLabel,
                            value: 'US',
                        },
                    ]}
                    id="country"
                    label={countryLabel}
                    customStyle={{ rootStyle: customFieldStyle }}
                    value={values.country}
                    onChange={(newValue) => setFieldValue('country', newValue)}
                    onBlur={() => setFieldTouched('country')}
                    errors={
                        touched.country &&
                        errors.country && [
                            {
                                id: 'country',
                                msg: errors.country,
                                isError: Boolean(errors.country),
                            },
                        ]
                    }
                    placeholder={selectLabel}
                    ariaRequired
                    disabled={!features['postalAddress']}
                    data-testid="country"
                />
                <TextInputWrapper>
                    <TextInput
                        id="address1"
                        label={address1Label}
                        placeholder={streetAddressLabel}
                        customStyle={{
                            rootStyle: {
                                width: '100%',
                            },
                        }}
                        value={values.streetAddress}
                        onClick={() => {
                            setShowCanadaPostAddressDropDown(true);
                        }}
                        onChange={(newValue) => {
                            setFieldValue('streetAddress', newValue);
                            if (values.country === 'CA') {
                                dispatch(GetCanadaPostAddressSuggestion(newValue, values.country, null));
                                setCanadaPostAddressDropdownActiveIndex(0);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.keyCode === KEYS.ENTER) {
                                // for enter key
                                e.preventDefault();
                                setCanadaPostAddressDropdownActiveIndex(0);
                                dispatch(ResetCanadaPostAddressSuggestion());

                                if (
                                    suggestionDropDownAddressList &&
                                    suggestionDropDownAddressList[canadaPostAddressDropdownActiveIndex]
                                ) {
                                    if (
                                        suggestionDropDownAddressList[canadaPostAddressDropdownActiveIndex].Next ===
                                        'Find'
                                    ) {
                                        dispatch(
                                            GetCanadaPostAddressSuggestion(
                                                values.streetAddress,
                                                values.country,
                                                suggestionDropDownAddressList[canadaPostAddressDropdownActiveIndex].Id
                                            )
                                        );
                                    } else {
                                        setFieldValue(
                                            'streetAddress',
                                            suggestionDropDownAddressList[canadaPostAddressDropdownActiveIndex].Text
                                        );
                                        dispatch(
                                            GetCanadaPostAddressDetails(
                                                suggestionDropDownAddressList[canadaPostAddressDropdownActiveIndex].Id
                                            )
                                        );
                                    }
                                } else {
                                    setShowCanadaPostAddressDropDown(false);
                                }
                            }
                            if (e.keyCode === KEYS.UP && canadaPostAddressDropdownActiveIndex !== 0) {
                                // for up arrow
                                setCanadaPostAddressDropdownActiveIndex(canadaPostAddressDropdownActiveIndex - 1);
                            }
                            if (
                                e.keyCode === KEYS.DOWN &&
                                canadaPostAddressDropdownActiveIndex !== suggestionDropDownAddressList.length
                            ) {
                                // for down arrow
                                setCanadaPostAddressDropdownActiveIndex(canadaPostAddressDropdownActiveIndex + 1);
                            }
                        }}
                        onBlur={() => {
                            setFieldTouched('streetAddress');
                        }}
                        errors={
                            touched.streetAddress &&
                            errors.streetAddress && [
                                {
                                    id: 'streetAddress',
                                    msg: errors.streetAddress,
                                    isError: Boolean(errors.streetAddress),
                                },
                            ]
                        }
                        ariaRequired
                        disabled={!features['postalAddress']}
                    />
                    {/* TO DO : https://jira.manulife.ca/browse/CDTMP-181 */}
                    {values.streetAddress &&
                        values.country !== 'US' &&
                        suggestionDropDownAddressList &&
                        suggestionDropDownAddressList.length > 0 &&
                        showCanadaPostAddressDropDown && (
                            <AutoSuggestionWrapper data-testid="dropDown" ref={dropDownRef}>
                                {isLoadingSuggestionDropDownAddressList && (
                                    <DropDownListLoadingIndicator data-testid="dropDownListLoadingIndicator" />
                                )}
                                {!isLoadingSuggestionDropDownAddressList &&
                                    suggestionDropDownAddressList.map((item, i) => {
                                        return i === canadaPostAddressDropdownActiveIndex ? (
                                            <AutoSuggestionListActive
                                                data-testid="dropDownElementActive"
                                                key={`${item.Text}-${i}`}
                                                onMouseDown={() => {
                                                    dispatch(ResetCanadaPostAddressSuggestion());
                                                    if (item.Next === 'Find') {
                                                        dispatch(
                                                            GetCanadaPostAddressSuggestion(
                                                                values.streetAddress,
                                                                values.country,
                                                                item.Id
                                                            )
                                                        );
                                                    } else {
                                                        setFieldValue('streetAddress', item.Text);
                                                        dispatch(GetCanadaPostAddressDetails(item.Id));
                                                    }
                                                }}
                                                ref={(el) => (scrollToDropDownActiveElement.current[i] = el)}
                                            >
                                                {item.Next === 'Find' ? (
                                                    <>
                                                        <AddressTextWrapper>
                                                            <AddressText> {item.Text} </AddressText>
                                                            <AddressDescription>
                                                                {' '}
                                                                {item.Description}{' '}
                                                            </AddressDescription>
                                                        </AddressTextWrapper>
                                                        <AddressIconWrapper>
                                                            <ButtonRightIcon fontSize="2rem" />
                                                        </AddressIconWrapper>
                                                    </>
                                                ) : (
                                                    <AddressTextWithoutIconWrapper>
                                                        <AddressText> {item.Text} </AddressText>
                                                        <AddressDescription> {item.Description} </AddressDescription>
                                                    </AddressTextWithoutIconWrapper>
                                                )}
                                            </AutoSuggestionListActive>
                                        ) : (
                                            <AutoSuggestionList
                                                data-testid="dropDownElement"
                                                key={`${item.Text}-${i}`}
                                                onMouseDown={() => {
                                                    dispatch(ResetCanadaPostAddressSuggestion());
                                                    if (item.Next === 'Find') {
                                                        dispatch(
                                                            GetCanadaPostAddressSuggestion(
                                                                values.streetAddress,
                                                                values.country,
                                                                item.Id
                                                            )
                                                        );
                                                    } else {
                                                        setFieldValue('streetAddress', item.Text);
                                                        dispatch(GetCanadaPostAddressDetails(item.Id));
                                                    }
                                                }}
                                                ref={(el) => (scrollToDropDownActiveElement.current[i] = el)}
                                            >
                                                {item.Next === 'Find' ? (
                                                    <>
                                                        <AddressTextWrapper>
                                                            <AddressText> {item.Text} </AddressText>
                                                            <AddressDescription>
                                                                {' '}
                                                                {item.Description}{' '}
                                                            </AddressDescription>
                                                        </AddressTextWrapper>
                                                        <AddressIconWrapper>
                                                            <ButtonRightIcon fontSize="2rem" />
                                                        </AddressIconWrapper>
                                                    </>
                                                ) : (
                                                    <AddressTextWithoutIconWrapper>
                                                        <AddressText> {item.Text} </AddressText>
                                                        <AddressDescription> {item.Description} </AddressDescription>
                                                    </AddressTextWithoutIconWrapper>
                                                )}
                                            </AutoSuggestionList>
                                        );
                                    })}
                                {!isLoadingSuggestionDropDownAddressList &&
                                    (canadaPostAddressDropdownActiveIndex === suggestionDropDownAddressList.length ? (
                                        <AutoSuggestionListActive
                                            data-testid="dropDownElementActiveTypeAddress"
                                            onMouseDown={() => {
                                                setShowCanadaPostAddressDropDown(false);
                                                dispatch(ResetCanadaPostAddressSuggestion());
                                            }}
                                            ref={(el) =>
                                                (scrollToDropDownActiveElement.current[
                                                    suggestionDropDownAddressList.length
                                                ] = el)
                                            }
                                        >
                                            <AddressNotFound>{addressNotFoundLabel}</AddressNotFound>
                                        </AutoSuggestionListActive>
                                    ) : (
                                        <AutoSuggestionList
                                            data-testid="dropDownElementTypeAddress"
                                            onMouseDown={() => {
                                                setShowCanadaPostAddressDropDown(false);
                                                dispatch(ResetCanadaPostAddressSuggestion());
                                            }}
                                            ref={(el) =>
                                                (scrollToDropDownActiveElement.current[
                                                    suggestionDropDownAddressList.length
                                                ] = el)
                                            }
                                        >
                                            <AddressNotFound>{addressNotFoundLabel}</AddressNotFound>
                                        </AutoSuggestionList>
                                    ))}
                            </AutoSuggestionWrapper>
                        )}
                    <TextInput
                        id="address2"
                        label={address2Label}
                        placeholder={apartmentOrUnitLabel}
                        customStyle={{
                            rootStyle: {
                                width: '100%',
                            },
                        }}
                        value={values.suite}
                        onChange={(newValue) => setFieldValue('suite', newValue)}
                        disabled={!features['postalAddress']}
                    />
                    <TextInput
                        id="city"
                        label={cityLabel}
                        placeholder={cityLabel}
                        customStyle={{
                            rootStyle: {
                                width: '100%',
                            },
                        }}
                        value={values.city}
                        onChange={(newValue) => setFieldValue('city', newValue)}
                        onBlur={() => setFieldTouched('city')}
                        errors={
                            touched.city &&
                            errors.city && [
                                {
                                    id: 'city',
                                    msg: errors.city,
                                    isError: Boolean(errors.city),
                                },
                            ]
                        }
                        ariaRequired
                        disabled={!features['postalAddress']}
                    />
                    <Dropdown
                        dropdownItems={[
                            {
                                label: albertProvinceLabel,
                                value: 'AB',
                            },
                            {
                                label: britishColumbiaProvinceLabel,
                                value: 'BC',
                            },
                            {
                                label: manitobaProvinceLabel,
                                value: 'MB',
                            },
                            {
                                label: newBrunswickProvinceLabel,
                                value: 'NB',
                            },
                            {
                                label: newfoundlandProvinceLabel,
                                value: 'NL',
                            },
                            {
                                label: novaScotiaProvinceLabel,
                                value: 'NS',
                            },
                            {
                                label: northwestTerritoriesProvinceLabel,
                                value: 'NT',
                            },
                            {
                                label: nunavutTerritoriesProvinceLabel,
                                value: 'NU',
                            },
                            {
                                label: ontarioProvinceLabel,
                                value: 'ON',
                            },
                            {
                                label: princeEdwardIslandProvinceLabel,
                                value: 'PE',
                            },
                            {
                                label: quebecProvinceLabel,
                                value: 'QC',
                            },
                            {
                                label: saskatchewanProvinceLabel,
                                value: 'SK',
                            },
                            {
                                label: yukonTerritoriesProvinceLabel,
                                value: 'YT',
                            },
                        ]}
                        id="province"
                        label={provinceLabel}
                        customStyle={{ rootStyle: customFieldStyle }}
                        value={values.province}
                        onChange={(newValue) => setFieldValue('province', newValue)}
                        onBlur={() => setFieldTouched('province')}
                        errors={
                            touched.province &&
                            errors.province && [
                                {
                                    id: 'province',
                                    msg: errors.province,
                                    isError: Boolean(errors.province),
                                },
                            ]
                        }
                        placeholder={selectLabel}
                        ariaRequired
                        disabled={!features['postalAddress']}
                    />
                    <TextInput
                        id="postalCode"
                        label={postalCodeLabel}
                        placeholder={postalCodePlaceholder}
                        mask="a9a 9a9"
                        customStyle={{
                            rootStyle: {
                                width: '100%',
                            },
                        }}
                        value={values.postalCode}
                        onChange={(newValue) => setFieldValue('postalCode', newValue.toUpperCase())}
                        onBlur={() => setFieldTouched('postalCode')}
                        errors={
                            touched.postalCode &&
                            errors.postalCode && [
                                {
                                    id: 'postalCode',
                                    msg: errors.postalCode,
                                    isError: Boolean(errors.postalCode),
                                },
                            ]
                        }
                        ariaRequired
                        disabled={!features['postalAddress']}
                    />
                    <TextInput
                        id="phoneNumber"
                        label={`${phoneNumberLabel} (${phoneNumberPlaceholder})`}
                        placeholder={phoneNumberPlaceholder}
                        mask="(999) 999-9999"
                        customStyle={{
                            rootStyle: {
                                width: '100%',
                            },
                        }}
                        value={values.phoneNumber}
                        onChange={(newValue) => setFieldValue('phoneNumber', newValue)}
                        onBlur={() => setFieldTouched('phoneNumber')}
                        errors={
                            touched.phoneNumber &&
                            errors.phoneNumber && [
                                {
                                    id: 'phoneNumber',
                                    msg: errors.phoneNumber,
                                    isError: Boolean(errors.phoneNumber),
                                },
                            ]
                        }
                        ariaRequired
                        disabled={!features['phoneNumber']}
                    />
                </TextInputWrapper>
                <ButtonWrapper>
                    <Button
                        id="cancelContactInfoButton"
                        variant={Button.VARIANT.SECONDARY}
                        onClick={() => {
                            sendAdobeAnalyticsTransaction('Edit My Personal Information');
                            dispatch(ResetCanadaPostAddressDetail());
                            dispatch(ResetCanadaPostAddressSuggestion());
                            history.push('/gb/member-profile-2/personal');
                        }}
                    >
                        {cancelButton}
                    </Button>
                    <Button
                        id="updateContactInfoButton"
                        type="submit"
                        disabled={
                            isSavingPostalAddressInformation ||
                            isCsr ||
                            isLoadingdropDownSelectedAddressDetail ||
                            (!features['phoneNumber'] && !features['postalAddress'])
                        }
                        saving={isSavingPostalAddressInformation}
                        onClick={() => {
                            sendAdobeAnalyticsTransaction('Edit My Personal Information');
                        }}
                    >
                        {saveButton}
                    </Button>
                </ButtonWrapper>
            </form>
        </Modal>
    );
};

export default ContactInfoModal;
